<!--
  专题页面 - Nuxt3版本 - 严格按照Nuxt2迁移
  @author NUXT专家
  @created 2024/12/xx
-->
<template>
  <NuxtLayout name="default">
    <div class="father">
      <div class="top">
        <a>首页</a>
        >
        <span>{{ topicName }}</span>
      </div>
      <div class="content">
        <SectionTitle
          h2
          :title="`为您推荐${topicName}相关展会`" />
        <div class="flex_con">
          <GoodsItem
            v-for="item in fairList"
            :key="item.id"
            :data="item"
            :industry-id="17"
            class="goods_exh" />
        </div>
      </div>
      <div
        v-if="newsList && newsList.length > 0"
        class="content">
        <SectionTitle
          h2
          :title="`为您推荐${topicName}相关资讯`" />
        <div class="flex_con">
          <div
            v-for="item in newsList"
            :key="item.id"
            class="news-item">
            <div class="cover-img">
              <img
                v-lazy="getWebp(item.coverImg)"
                :alt="item.title" />
            </div>
            <div class="bot">
              <div class="title">
                <NuxtLink
                  :to="handleNewsUrl(item.viewUrl, item.id)"
                  :title="item.title">
                  {{ item.title }}
                </NuxtLink>
              </div>
              <div class="desc">{{ item.content }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="tag-box">
        <SectionTitle
          h2
          title="推荐专题" />
        <div class="tag-inner">
          <div
            v-for="item in list"
            :key="item.id"
            class="tag-item">
            <NuxtLink
              :to="`/special/${item.id}.html`"
              :title="item.specialTopicName"
              target="_blank">
              {{ item.specialTopicName }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
  /**
   * 专题页面 - Nuxt3版本
   * @author NUXT专家
   * @created 2024/12/xx
   */

  import { ref } from 'vue'
  import _ from 'lodash'
  import { useLang } from '~/composables/useLang'
  import { useHttp } from '~/composables/useHttp'
  import { ResponseCode } from '~/utils/constant'
  import { handleNewsUrl, getWebp } from '~/utils/common'

  // 异步组件导入
  const GoodsItem = defineAsyncComponent(() => import('~/components/Goods/item.vue'))
  const SectionTitle = defineAsyncComponent(() => import('~/components/SectionTitle/index.vue'))

  // 设置语言
  useLang('cn' as any)

  // 路由参数
  const route = useRoute()
  const { id } = route.params

  // 响应式数据
  const topicName = ref('')
  const fairList = ref<any[]>([])
  const newsList = ref<any[]>([])
  const list = ref<any[]>([])
  const seo = ref<any>({})

  // 工具方法
  const handleNewsUrlMethod = (viewUrl: string, newsId: string) => {
    return handleNewsUrl(viewUrl, newsId)
  }

  // 获取专题详情和推荐列表的并行请求
  const fetchData = async () => {
    try {
      const [topicRes, listRes] = await Promise.all([
        useHttp.get(`/api/specialTopicApi/${id}`),
        useHttp.post('/api/specialTopicApi/top', { pageNo: 1, pageSize: 20 })
      ])

      // 处理专题详情
      if (topicRes.code === ResponseCode.SUCCESS && topicRes.data) {
        seo.value = {
          seoTitle: topicRes.data.seoTitle,
          seoKeyword: topicRes.data.seoKeyword,
          seoDesc: topicRes.data.seoDesc
        }
        topicName.value = topicRes.data.specialTopicName
        fairList.value = topicRes.data.exhibitionList || []

        // 处理新闻列表
        if (topicRes.data.newsList && topicRes.data.newsList.length > 0) {
          const newsPromises = topicRes.data.newsList.map((item: any) => {
            return useHttp.get(`/ossApi/newsContent/${item.id}.html`)
          })

          try {
            const contents = await Promise.all(newsPromises)

            newsList.value = topicRes.data.newsList.map((item: any, index: number) => {
              const reg = /<[^>]+>/gi
              if (_.isString(contents[index])) {
                item.content = (contents[index] as string)
                  ?.replace(reg, '')
                  ?.replace(/&nbsp;/g, '')
                  ?.trim()
              } else {
                item.content = ''
              }
              return item
            })
          } catch (error) {
            console.error('获取新闻内容失败:', error)
            newsList.value = topicRes.data.newsList || []
          }
        } else {
          newsList.value = []
        }
      } else {
        // 如果专题不存在，抛出404错误
        throw createError({
          statusCode: 404,
          statusMessage: 'Page not found'
        })
      }

      // 处理推荐专题列表
      if (listRes.code === ResponseCode.SUCCESS) {
        list.value = listRes.data.records || []
      } else {
        list.value = []
      }
    } catch (error) {
      console.error('获取专题数据失败:', error)
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found'
      })
    }
  }

  // SSR 数据获取
  try {
    await fetchData()
  } catch (error) {
    // SSR 阶段的错误处理
    throw createError({
      statusCode: 404,
      statusMessage: 'Page not found'
    })
  }

  // 页面挂载后，如果没有数据则重新获取
  onMounted(() => {
    if (!topicName.value && id) {
      fetchData()
    }
  })

  // 页面头部信息
  useHead({
    title: seo.value?.seoTitle || `${topicName.value}-聚展`,
    meta: [
      {
        name: 'keywords',
        content: seo.value?.seoKeyword || ''
      },
      {
        name: 'description',
        content: seo.value?.seoDesc || ''
      }
    ]
  })
</script>

<style lang="less" scoped>
  .father {
    width: 1190px;
    margin: 0 auto;

    .top {
      background-color: #fff;
      padding: 19px;
    }

    .content {
      margin: 20px 0 10px 0;

      .flex_con {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        margin-top: 10px;

        .news-item {
          background-color: #fff;
          border-radius: 6px;
          display: flex;
          align-content: center;
          padding: 16px 13px;
          width: 588px;
          margin-bottom: 14px;

          .cover-img {
            width: 210px;
            height: 117px;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            margin-right: 8px;
            border-radius: 4px;

            img {
              border-radius: 4px;
              width: 210px;
              min-height: 100%;
              height: auto;
            }
          }

          .bot {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
          }

          .title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 349px;
          }

          .desc {
            font-size: 14px;
            color: #666;
            margin-top: 20px;
            width: 349px;
            -webkit-line-clamp: 3; // 用来限制在一个块元素显示的文本的行数
            display: -webkit-box; // 将对象作为弹性伸缩盒模型显示
            -webkit-box-orient: vertical; //设置或检查伸缩盒对象的子元素的排列方式
            text-overflow: ellipsis; // 在多行文本的情况下，用...隐藏超出范围的文本
            word-break: break-all;
            overflow: hidden;
          }
        }
      }
    }

    .tag-box {
      .tag-inner {
        display: flex;
        flex-wrap: wrap;
        margin-top: 16px;

        .tag-item {
          padding: 8px 16px;
          background: #fff;
          border-radius: 4px;
          margin: 0 12px 12px 0;
        }
      }
    }
  }
</style>
